import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { saveAs } from 'file-saver';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { SkeletonModule } from 'primeng/skeleton';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { KNOWLEDGE_SCOPE } from 'src/app/contract-management/models/contract-management.model';
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { CheckboxComponent } from "../../../../../@core/components/ui/checkbox/checkbox.component";
import { DeleteConfirmationComponent } from "../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";
import { DialogComponent } from "../../../../../@core/components/ui/dialog/dialog.component";
import { PopoverComponent } from "../../../../../@core/components/ui/popover/popover.component";
import { IAttachment, IFile } from '../../../../../@core/models/model';
import { ToastService } from "../../../../../@core/services/toast.service";
import { DataTableComponent } from '../../../../../@shared/components/data-table/data-table.component';
import { DocumentPreviewComponent } from "../../../../../@shared/components/document-preview/document-preview.component";
import { FileDropComponent } from "../../../../../@shared/components/file-drop/file-drop.component";
import { FileDropTypes } from '../../../../../@shared/components/file-drop/file-drop.types';
import { SearchBarComponent } from '../../../../../@shared/components/search-bar/search-bar.component';
import { UploadDocsComponent } from '../../../../../@shared/components/upload-docs/upload-docs.component';
import { TableColumn, TableConfig } from '../../../../../@shared/models/table.models';
import { SanitizeHtmlPipe } from "../../../../../@shared/pipes/sanitizeHtml.pipe";
import { IKnowledgeBaseStore, KNOWLEDGE_BASE_STORE_STATUS, KnowledgeBaseService } from '../../../../services/knowledgebase.service';
import { RepositoryService } from '../../../../services/repository.service';
import { KNOWLEDGE_BASE_SVG } from '../knowledge-base-svg';


@Component({
  selector: 'app-knowledge-base-store',
  imports: [
    CommonModule,
    AvatarModule,
    DatePipe,
    SkeletonModule,
    RouterModule,
    ButtonModule,
    FormsModule,
    Textarea,
    InputTextModule,
    ReactiveFormsModule,
    FileDropComponent,
    DataTableComponent,
    PaginatorModule,
    SanitizeHtmlPipe,
    CardModule,
    InputGroupModule,
    InputGroupAddonModule,
    CheckboxModule,
    FormsModule,
    DocumentPreviewComponent,
    SearchBarComponent,
    TitleCasePipe,
    TooltipModule,
    ButtonComponent,
    DialogComponent,
    CheckboxComponent,
    PopoverComponent,
    DeleteConfirmationComponent,
    PanelModule,
    InfiniteScrollDirective,
    UserNameDisplayComponent
  ],
  standalone: true,
  templateUrl: './knowledge-base-store.component.html',
  styleUrl: './knowledge-base-store.component.scss'
})
export class KnowledgeBaseStoreComponent {

  @Input() projectId: string | null = null;
  @Input() scope: KNOWLEDGE_SCOPE = KNOWLEDGE_SCOPE.WORKSPACE;


  @ViewChild('editableContent', { static: false }) editableContent!: ElementRef;

  // Template References for Data Table
  @ViewChild('fileNameTemplate') fileNameTemplate!: TemplateRef<any>;
  @ViewChild('addedByTemplate') addedByTemplate!: TemplateRef<any>;
  @ViewChild('timesUsedTemplate') timesUsedTemplate!: TemplateRef<any>;
  @ViewChild('addedOnTemplate') addedOnTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<any>;
  @ViewChild('detailPanel') detailPanel!: any;

  knowledgeBaseStore: any[] = [];
  storeLoading = false;
  selectedStore: IKnowledgeBaseStore | null = null;
  knowledgeBaseStoreAddDialogVisible = false;
  knowledgeBaseStoreForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    files: new FormControl(null, [Validators.required]),
  });

  knowledgeBaseStoreFormControls = {
    name: this.knowledgeBaseStoreForm.controls['name'],
    description: this.knowledgeBaseStoreForm.controls['description'],
    files: this.knowledgeBaseStoreForm.controls['files'],
  };
  KNOWLEDGE_BASE_STORE_STATUS = KNOWLEDGE_BASE_STORE_STATUS;
  isDetailsCollapsed: boolean = false;
  isEditing = false;
  SVGS = KNOWLEDGE_BASE_SVG;
  KNOWLEDGE_SCOPE = KNOWLEDGE_SCOPE;
  showUploadDrop = false;
  editedStore = { name: '', description: '' };
  selectedFiles: any[] = [];
  showDocumentPreview: boolean = false;
  selectedDocumentFile: IAttachment | null = null;
  selectedDocumentId: string | null = null;
  showDeLinkPanel: boolean = false
  deLinkItemId: any
  deLinkItemStoreId: IKnowledgeBaseStore | null = null
  showDeleteDialogBox: boolean = false;
  deleteFileId: any
  currentOpenPopover: any = null;
  selectedDeleteFileIds: any
  selectedKnowledgeBase: any
  selectedFile: IFile | null = null;
  showDeleteKnowledgeBaseStoreDialog: boolean = false;
  openEditKnowledgeBasePanel: boolean = false
  openAddFileToKnowledgeBase: boolean = false
  openLinkExistingFilePanel: boolean = false
  allFiles: IFile[] = [];
  filteredFilesForLinking: IFile[] = [];
  selectedFilesForLinking: IFile[] = [];
  linkingSearchValue: string = '';
  filesSearchValue: string = '';
  filesForLinkingLoading: boolean = false;
  preserveSelectionsOnClose: boolean = false;
  filteredStoreFiles: any[] = [];
  files: File[] = [];
  loadingMore = false;
  hasMoreData = true;
  skip = 0;
  limit = 15;
  total = 0;

  public allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC];

  // Data Table Configuration
  tableConfig: TableConfig = {
    selectable: true,
    paginator: false,
    rows: 5,
    loading: true,
    emptyMessage: 'No files linked to this knowledge base'
  };

  tableColumns: TableColumn[] = [
    {
      field: 'file.name',
      header: 'File',
      type: 'custom',
      template: 'fileNameTemplate',
      width: '30%'
    },
    {
      field: 'createdBy',
      header: 'Added By',
      type: 'custom',
      template: 'addedByTemplate',
      width: "20%"
    },
    {
      field: 'numberOfTimesUsed',
      header: 'No of times used',
      type: 'custom',
      template: 'timesUsedTemplate',
      width: "10%"
    },
    {
      field: 'file.createdAt',
      header: 'Added on',
      type: 'custom',
      template: 'addedOnTemplate',
      width: "20%"
    },
    {
      field: 'actions',
      header: 'Action',
      type: 'custom',
      template: 'actionsTemplate',
      width: "20%"
    }
  ];

  constructor(
    private knowledgeBaseService: KnowledgeBaseService,
    private toast: ToastService,
    private repositoryService: RepositoryService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private socketService: SocketService
  ) {
    this.socketService.on<{ knowledgeBaseStoreId: string, success: boolean, message: string, knowledgeBase: IKnowledgeBaseStore }>(SOCKET_EVENTS.KNOWLEDGE_BASE_UPLOAD_COMPLETED).subscribe((data) => {
      if (data && data[0]) {
        if (data[0].success && data[0].knowledgeBase?.status === KNOWLEDGE_BASE_STORE_STATUS.READY) {
          const storeIndex = this.knowledgeBaseStore.findIndex(store => store._id === data[0].knowledgeBaseStoreId);
          if (storeIndex !== -1) {
            this.knowledgeBaseStore[storeIndex] = data[0].knowledgeBase;
          }
          if (this.selectedStore?._id === data[0].knowledgeBaseStoreId) {
            this.selectedStore = data[0].knowledgeBase;
            // Update filtered files when store is updated via socket
            if (this.filesSearchValue.trim()) {
              this.searchFiles();
            } else {
              this.filteredStoreFiles = this.selectedStore.files ? [...this.selectedStore.files] : [];
            }
            this.tableConfig = { ...this.tableConfig, loading: false };
            this.cdr.detectChanges();
          }
        } else {
          this.toast.error(data[0].message || 'Failed to upload knowledge base');
        }
      }
    });
  }

  get templatesMap(): { [key: string]: TemplateRef<any> } {
    return {
      'fileNameTemplate': this.fileNameTemplate,
      'addedByTemplate': this.addedByTemplate,
      'timesUsedTemplate': this.timesUsedTemplate,
      'addedOnTemplate': this.addedOnTemplate,
      'actionsTemplate': this.actionsTemplate
    };
  }

  ngOnInit() {
    this.getKnowledgeBaseStore(true);
    this.editedStore = {
      name: this.selectedStore?.name || '',
      description: this.selectedStore?.description || ''
    };
  }

  getKnowledgeBaseStore(isInitialLoad = false, searchTerm = '', selectNewest = false) {
    if (isInitialLoad) {
      this.storeLoading = true;
      this.tableConfig = { ...this.tableConfig, loading: true };
      this.skip = 0;
      this.knowledgeBaseStore = [];
    } else {
      this.tableConfig.loading = true;
      this.loadingMore = true;
    }

    const filters = {
      search: searchTerm,
      skip: this.skip,
      limit: this.limit,
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {})
    };

    this.knowledgeBaseService.getKnowledgeBaseStore(filters).subscribe((res) => {
      if (res && res.success) {
        this.total = res.data?.total || 0;
        const newStores = res.data?.stores || [];

        if (isInitialLoad) {
          this.knowledgeBaseStore = newStores;
          if (selectNewest && this.knowledgeBaseStore.length > 0) {
            this.selectedStore = this.knowledgeBaseStore[0];
          } else {
            this.selectedStore = this.selectedStore || this.knowledgeBaseStore?.length > 0 ? this.knowledgeBaseStore[0] : null;
          }
          // Initialize filtered files when store is selected
          if (this.selectedStore) {
            this.filteredStoreFiles = this.selectedStore.files ? [...this.selectedStore.files] : [];
          }
          this.tableConfig = {
            ...this.tableConfig,
            loading: this.selectedStore?.status === KNOWLEDGE_BASE_STORE_STATUS.UPLOADING
          };
        } else {
          this.knowledgeBaseStore = [...this.knowledgeBaseStore, ...newStores];
        }

        this.skip += this.limit;
        this.hasMoreData = this.knowledgeBaseStore.length < this.total;

        this.storeLoading = false;
        this.loadingMore = false;
        this.cdr.detectChanges();
      } else {
        this.tableConfig = { ...this.tableConfig, loading: false };
        this.storeLoading = false;
        this.loadingMore = false;
        this.toast.error('Failed to fetch knowledge base store');
      }
    });
  }

  onScroll() {
    this.loadMore();
  }

  loadMore() {
    if (!this.loadingMore && this.hasMoreData && !this.storeLoading) {
      this.getKnowledgeBaseStore(false);
    }
  }

  updateSelectedKnowledgeBaseStore() {
    if (!this.selectedStore?._id) return;
    this.knowledgeBaseService.getKnowledgeBaseStoreById(this.selectedStore._id).subscribe((res) => {
      if (res && res.success) {
        this.selectedStore = res.data;
        const storeIndex = this.knowledgeBaseStore.findIndex(store => store._id === this.selectedStore?._id);
        if (storeIndex !== -1) {
          this.knowledgeBaseStore[storeIndex] = res.data;
        }
        // Update filtered files when store is updated
        if (this.filesSearchValue.trim()) {
          this.searchFiles();
        } else {
          this.filteredStoreFiles = this.selectedStore.files ? [...this.selectedStore.files] : [];
        }
      }
    }
    );
  }

  togglePeopleTrackerPanel(event: Event) {
    event.stopPropagation();
    this.isDetailsCollapsed = !this.isDetailsCollapsed;
    this.detailPanel.toggle(event);
  }

  onSelectionChange(event: any) {
    this.selectedFiles = Array.isArray(event) ? event : [];
  }

  downloadSelected() {
    if (this.selectedFiles.length > 0) {
      const selectedFileIds = this.selectedFiles.map(file => file.file._id);
      if (selectedFileIds.length > 0) {
        this.repositoryService.downloadRepoFiles(selectedFileIds).subscribe(res => {
          if (res.success && res.data) {
            const blob = new Blob([new Uint8Array(res.data.data)], { type: 'application/x-zip-compressed' });
            saveAs(blob, 'InnDoc AI Linked Files Download.zip');
            this.selectedFiles = [];
          }
        })
      }
    }
  }

  deleteSelected(event: Event) {
    // TODO : write bulk delete API call 
    if (this.selectedFiles.length === 0) return;
    this.toast.success(`${this.selectedFiles.length} file(s) deleted successfully`);
    this.selectedFiles = [];
    this.getKnowledgeBaseStore();
  }

  selectStore(store: IKnowledgeBaseStore) {
    if (this.selectedStore?._id !== store._id) {
      this.selectedStore = store;
      this.filesSearchValue = '';
      this.filteredStoreFiles = store.files ? [...store.files] : [];
      this.tableConfig = {
        ...this.tableConfig,
        loading: this.selectedStore?.status === KNOWLEDGE_BASE_STORE_STATUS.UPLOADING
      };
      this.cdr.detectChanges();
    }
  }

  openDeleteDialog() {
    if (this.selectedStore?._id) {
      this.showDeleteKnowledgeBaseStoreDialog = true;
    }
  }

  handlePopoverOpen(popover: any) {
    if (this.currentOpenPopover && this.currentOpenPopover !== popover) {
      this.currentOpenPopover.hide();
    }
    this.currentOpenPopover = popover;
  }

  editKnowledgeBaseDetails() {
    if (!this.selectedStore) return;

    this.editedStore = {
      name: this.selectedStore.name,
      description: this.selectedStore.description
    };
    this.openEditKnowledgeBasePanel = true;
  }


  renameKnowledgeBase() {
    if (!this.selectedStore?._id) return;
    this.knowledgeBaseService.updateKnowledgeBaseStore(this.selectedStore._id, {
      name: this.editedStore.name,
      description: this.editedStore.description
    }).subscribe((res) => {
      if (res) {
        this.toast.success('Knowledge base updated successfully');
        this.getKnowledgeBaseStore(true);
        this.openEditKnowledgeBasePanel = false;
      } else {
        this.toast.error('Failed to update knowledge base');
      }
    })
  }

  resetCreateKnowledgeBaseDialog() {
    this.knowledgeBaseStoreForm.reset();
    this.files = [];
    this.selectedFilesForLinking = [];
    this.showUploadDrop = false;
  }


  handleKnowledgeBaseStoreAddDialogVisible(visible: boolean) {
    this.knowledgeBaseStoreAddDialogVisible = visible;
    if (!visible) {
      this.resetCreateKnowledgeBaseDialog();
    }
  }


  createKnowledgeBaseStore() {
    this.storeLoading = true;
    const formData = new FormData();
    formData.append('name', this.knowledgeBaseStoreForm.value.name);
    formData.append('description', this.knowledgeBaseStoreForm.value.description);
    formData.append('scope', this.scope);
    if (this.projectId) {
      formData.append('project', this.projectId);
    }
    this.selectedFilesForLinking.forEach(file => {
      formData.append('files', file._id);
    });
    if (this.files.length) {
      this.files.forEach(file => {
        formData.append('files', file);
      })
    }

    this.handleKnowledgeBaseStoreAddDialogVisible(false);

    this.knowledgeBaseService.createKnowledgeBaseStore(formData).subscribe((res) => {
      if (res.success && res.data) {
        this.toast.success('Knowledge Base Store created successfully');
        this.storeLoading = false;

        if (res.data._id) {
          this.knowledgeBaseService.getKnowledgeBaseStoreById(res.data._id).subscribe((storeRes) => {
            if (storeRes && storeRes.success) {
              this.knowledgeBaseStore.unshift(storeRes.data);
              this.total += 1;
              this.selectedStore = storeRes.data;
              this.filteredStoreFiles = this.selectedStore.files ? [...this.selectedStore.files] : [];
              if (this.selectedStore.status === KNOWLEDGE_BASE_STORE_STATUS.UPLOADING) {
                this.toast.info('Files are being uploaded. This may take a while.');
                this.tableConfig = { ...this.tableConfig, loading: true };
              } else {
                this.tableConfig = { ...this.tableConfig, loading: false };
              }
              this.cdr.detectChanges();
            } else {
              this.getKnowledgeBaseStore(true, '', true);
            }
          });
        } else {
          this.getKnowledgeBaseStore(true, '', true);
        }
      } else {
        this.storeLoading = false;
      }
    });
  }

  onFileUploaded(event: File | File[]) {
    event = event as File[]
    if (event.length) {
      this.files = event;
    }
    else {
      this.files = [];
    }
  }

  onFileRemove(file: File) {
    this.files = this.files.filter(f => f !== file);
  }

  toggleEdit() {
    this.isEditing = !this.isEditing;
    if (this.isEditing) {
      setTimeout(() => {
        this.editableContent.nativeElement.focus();
      });
    }
  }

  blurSpan(event: Event) {
    const target = event.target as HTMLElement;
    target.blur();
    this.isEditing = false;
  }

  updateStoreName(event: Event) {
    event.preventDefault();
    const target = event.target as HTMLElement;
    const newName = target.textContent?.trim() || 'Untitled';
    target.innerHTML = newName;
    this.isEditing = false;

    if (this.selectedStore && this.selectedStore.name !== newName) {
      this.selectedStore.name = newName;
      this.knowledgeBaseService.updateKnowledgeBaseStore(this.selectedStore._id, { name: newName }).subscribe({
        next: (res: { success: boolean; data: IKnowledgeBaseStore }) => {
          if (res.success) {
            this.toast.success('Store name updated successfully');
          }
        },
        error: (err: unknown) => {
          this.toast.error('Failed to update store name');
          if (this.selectedStore) {
            target.innerHTML = this.selectedStore.name;
          }
        }
      });
    }
  }

  deleteStore(_id: string) {
    this.showDeleteKnowledgeBaseStoreDialog = false;
    this.knowledgeBaseService.deleteKnowledgeBaseStore(_id).subscribe((res) => {
      this.toast.success('Store deleted successfully');
      this.getKnowledgeBaseStore(true);
    });

  }

  openDeleteDialogBox(event?: Event, _id?: string, storeId?: string) {
    this.deleteFileId = _id,
      this.selectedKnowledgeBase = storeId,
      this.showDeleteDialogBox = true
  }


  deleteFiles(fileid?: string, storeId?: string) {
    const fileIds: string[] = []
    const KnowledgeBaseStoreId = storeId ? storeId : this.selectedStore?._id;
    if (fileid) {
      fileIds.push(fileid);
    } else if (this.selectedFiles.length > 0) {
      fileIds.push(...this.selectedFiles.map(file => file.file._id));
    }
    this.knowledgeBaseService.deleteFilesFromRepo(fileIds, KnowledgeBaseStoreId).subscribe((res) => {
      if (res.success) {
        this.toast.success('File(s) deleted successfully');
        this.selectedFiles = [];
        // Update filtered files after deletion
        if (this.selectedStore) {
          this.selectedStore.files = this.selectedStore.files.filter((file: any) => !fileIds.includes(file.file._id));
          if (this.filesSearchValue.trim()) {
            this.searchFiles();
          } else {
            this.filteredStoreFiles = [...this.selectedStore.files];
          }
        }
        this.getKnowledgeBaseStore(true);
      } else {
        this.toast.error('Failed to delete file(s)');
      }
      this.showDeleteDialogBox = false;
    });
  }

  openDelinkPanel(event?: Event, _id?: string, store?: IKnowledgeBaseStore) {
    this.deLinkItemStoreId = store ?? this.selectedStore;
    this.showDeLinkPanel = true;
    this.deLinkItemId = _id;
  }

  deLinkFile(ids: string[], store: IKnowledgeBaseStore | null) {
    if (!store || !ids.length) return;
    let fileIds: string[] = ids
    if (this.selectedFiles.length > 0) {
      fileIds = this.selectedFiles.map(file => file.file._id);
    }
    this.knowledgeBaseService.delinkFiles(fileIds, store._id).subscribe((res) => {
      if (res.success) {
        this.toast.success('File(s) de-linked successfully');
        if (this.selectedStore) {
          this.selectedStore.files = this.selectedStore.files.filter(file => !ids.includes(file.file._id));
          // Update filtered files after delinking
          if (this.filesSearchValue.trim()) {
            this.searchFiles();
          } else {
            this.filteredStoreFiles = [...this.selectedStore.files];
          }
        }
        this.selectedFiles = [];
        this.getKnowledgeBaseStore(true);
        this.showDeLinkPanel = false;
      } else {
        this.toast.error('Failed to de-link file(s)');
        this.showDeLinkPanel = false;
      }

    });
  }

  openLinkFilesDialog() {
    if (this.files.length === 0) {
      this.showUploadDrop = false;
    }
    this.openLinkExistingFiles();
  }


  openAddFileInKnowledgeBase() {
    this.openAddFileToKnowledgeBase = true
  }

  uploadFile() {
    this.openAddFileToKnowledgeBase = false
    this.dialog.open(UploadDocsComponent, {
      data: {
        origin: 'forKnowledgeBase',
        type: 'file',
        customTitle: `Upload File to Knowledge Base ${this.selectedStore?.name}`,
        customDescription: `Upload your files to the knowledge base "${this.selectedStore?.name}"`,
        knowledgeBaseStoreId: this.selectedStore?._id,
        scope: this.scope,
        project: this.projectId
      },
      width: '36rem',
    }).afterClosed().subscribe((res) => {
      if (res) {
        this.updateSelectedKnowledgeBaseStore();
      }
    });
  }

  openLinkExistingFiles() {
    this.openLinkExistingFilePanel = true;
    this.getAllFilesForLinking();
  }

  getAllFilesForLinking() {
    this.filesForLinkingLoading = true;
    const params = {
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.projectId as string } : {}),
      scope: this.scope
    }
    this.repositoryService.getFiles(params).subscribe((res) => {

      this.allFiles = res.data.filter(file =>
        file.type === 'FILE'
      );
      this.filteredFilesForLinking = [...this.allFiles];
      this.filesForLinkingLoading = false;
    });
  }

  checkIfFileIsLinked(file: IFile) {
    return this.selectedFilesForLinking.some(f => f._id === file._id);
  }

  removeSelectedFile(file: IFile) {
    this.selectedFilesForLinking = this.selectedFilesForLinking.filter(f => f !== file);
  }

  searchFiles() {
    if (!this.selectedStore?.files) {
      this.filteredStoreFiles = [];
      return;
    }

    if (!this.filesSearchValue.trim()) {
      this.filteredStoreFiles = [...this.selectedStore.files];
      return;
    }

    const searchTerm = this.filesSearchValue.toLowerCase().trim();

    this.filteredStoreFiles = this.selectedStore.files.filter((item: any) => {
      const file = item.file;
      return (
        file?.name?.toLowerCase().includes(searchTerm) ||
        file?.mimeType?.toLowerCase().includes(searchTerm) ||
        item.createdBy?.fullName?.toLowerCase().includes(searchTerm)
      );
    });
  }

  onLinkingSearch() {
    console.log(this.linkingSearchValue);
    if (!this.linkingSearchValue.trim()) {
      this.filteredFilesForLinking = [...this.allFiles];
      this.selectedFilesForLinking = [];
      return;
    }

    const searchTerm = this.linkingSearchValue.toLowerCase().trim();

    this.filteredFilesForLinking = this.allFiles.filter(file => {
      return (
        file.name?.toLowerCase().includes(searchTerm) ||
        file.mimeType?.toLowerCase().includes(searchTerm) ||
        file.createdBy?.fullName?.toLowerCase().includes(searchTerm)
      );
    });

    console.log(this.filteredFilesForLinking);
    this.selectedFilesForLinking = [];
  }

  getKnowledgeBasesForFile(file: IFile): Array<{ _id: string, name: string }> {
    return file.knowledgeBases && file.knowledgeBases.length > 0
      ? file.knowledgeBases.map((kb: IKnowledgeBaseStore) => ({ _id: kb._id, name: kb.name }))
      : [];
  }

  closeLinkExistingFilePanel() {
    this.openLinkExistingFilePanel = false;
    if (!this.preserveSelectionsOnClose) {
      this.selectedFilesForLinking = [];
    }
    this.linkingSearchValue = '';
    this.filteredFilesForLinking = [];
    this.preserveSelectionsOnClose = false;
  }

  toggleSelectAll(event: any) {
    if (event.checked) {
      this.selectedFilesForLinking = [...this.filteredFilesForLinking];
    } else {
      this.selectedFilesForLinking = [];
    }
  }

  toggleFileSelection(file: IFile, event: any) {
    if (event.checked) {
      if (!this.selectedFilesForLinking.includes(file)) {
        this.selectedFilesForLinking.push(file);
      }
    } else {
      const index = this.selectedFilesForLinking.findIndex(f => f._id === file._id);
      if (index > -1) {
        this.selectedFilesForLinking.splice(index, 1);
      }
    }
  }

  linkSelectedFilesToStore() {

    if (this.knowledgeBaseStoreAddDialogVisible === true) { // if this dialog is open means we are in create box
      this.preserveSelectionsOnClose = true;
      this.openLinkExistingFilePanel = false;
      this.filteredFilesForLinking = this.filteredFilesForLinking.filter(file => !this.selectedFilesForLinking.includes(file));
      this.linkingSearchValue = '';
    }
    else {
      if (this.selectedFilesForLinking.length === 0) {
        this.toast.error('Please select at least one file');
        return;
      }

      if (!this.selectedStore?._id) {
        this.toast.error('No knowledge base selected');
        return;
      }

      const selectedFileIds = this.selectedFilesForLinking.map(file => file._id);

      this.repositoryService.linkToKnowledgeBase([this.selectedStore._id], selectedFileIds).subscribe({
        next: (res) => {
          if (res.success) {
            const allAlreadyLinked = res.data?.results?.every((result: any) =>
              !result.success && result.message?.includes('were already linked')
            );

            if (allAlreadyLinked) {
              this.toast.info('Selected files were already linked to this knowledge base');
            } else {
              this.toast.success('Files linked to knowledge base successfully');
            }
            this.selectedFilesForLinking = [];
            this.linkingSearchValue = '';
            this.preserveSelectionsOnClose = true;
            this.openLinkExistingFilePanel = false;
            this.openAddFileToKnowledgeBase = false;
            this.updateSelectedKnowledgeBaseStore();
          }
        },
        error: (err) => {
          this.toast.error('Failed to link files to knowledge base');
        }
      });
    }
  }

  openDocumentPreview(file: IFile) {
    this.selectedFile = file;
    this.selectedDocumentFile = file.attachment as IAttachment
    this.selectedDocumentId = file._id
    this.showDocumentPreview = true;
  }

  closeDocumentPreview() {
    this.selectedFile = null;
    this.selectedDocumentFile = null;
    this.selectedDocumentId = null;
    this.showDocumentPreview = false;
  }

}
